import { isValidCui, normalizeCui } from '@elsa/contracts';
import { Button, ErrorBox, Field, Input, Link, Note, navigate } from '@elsa/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Logo } from '../brand.tsx';
import { rememberOrg } from '../lib/session.tsx';
import { app, callFunction, friendly, must, rpc, supabase } from '../lib/supabase.ts';
import { Footer } from '../pages/PublicLayout.tsx';

// Firma noua: CUI -> datele din ANAF (Edge Function anaf-cui) -> app.create_organization. Cine o
// creeaza devine proprietar, iar perioada de proba (14 zile) porneste automat (billing).

interface Company {
  cui: string;
  name: string;
  reg_com: string | null;
  address: string | null;
  county: string | null;
  city: string | null;
  vat_payer: boolean;
  inactive: boolean;
}

export function OnboardingPage({ first, email }: { first: boolean; email: string }) {
  const qc = useQueryClient();
  const [cui, setCui] = useState('');
  const [company, setCompany] = useState<Partial<Company>>({});
  const [lookup, setLookup] = useState<'idle' | 'busy' | 'found' | 'notfound' | 'manual'>('idle');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const valid = isValidCui(cui);

  async function find() {
    setError(null);
    setLookup('busy');
    try {
      const r = await callFunction<{ company: Company | null }>('anaf-cui', { cui });
      if (r.company) {
        setCompany(r.company);
        setLookup('found');
      } else {
        setCompany({ cui: normalizeCui(cui) });
        setLookup('notfound');
      }
    } catch (err) {
      setError(err);
      setCompany({ cui: normalizeCui(cui) });
      setLookup('manual');
    }
  }

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const s = (k: string) => {
      const v = String(f.get(k) ?? '').trim();
      return v ? v : null;
    };
    setBusy(true);
    setError(null);
    try {
      const org = await rpc<string>('create_organization', {
        p_name: s('name'),
        p_cui: cui,
        p_billing_email: s('billing_email'),
        p_reg_com: s('reg_com'),
        p_address: s('address'),
        p_county: s('county'),
        p_city: s('city'),
        p_job_title: s('job_title'),
      });
      if (company.vat_payer !== undefined) {
        await must(
          app().from('organizations').update({ vat_payer: company.vat_payer }).eq('id', org),
        );
      }
      rememberOrg(org);
      // JWT-ul nou are firma in `orgs` (hook-ul de token); RLS-ul nu depinde de el.
      await supabase.auth.refreshSession();
      await qc.invalidateQueries();
      navigate('/app');
    } catch (err) {
      setError(new Error(friendly((err as Error).message)));
    } finally {
      setBusy(false);
    }
  }

  const ready = lookup === 'found' || lookup === 'notfound' || lookup === 'manual';
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex items-center justify-between border-b-2 border-grafit bg-cod px-4 py-3 text-hartie">
        <Logo />
        <div className="flex items-center gap-3 text-sm">
          {!first ? (
            <Link to="/app" className="underline">
              Înapoi
            </Link>
          ) : null}
          <button type="button" className="underline" onClick={() => void supabase.auth.signOut()}>
            Ieși
          </button>
        </div>
      </header>
      <main className="mx-auto w-full max-w-lg flex-1 space-y-4 px-4 py-8">
        <div>
          <h1 className="text-[28px] leading-tight font-extrabold tracking-tight">
            {first ? 'Bun venit în ELSA!' : 'Adaugă o firmă'}
          </h1>
          <p className="mt-1 text-sm text-gri-800">
            Elsa e pentru firme: contul și abonamentul sunt ale firmei, iar tu îi poți invita pe
            colegi. Dacă firma ta are deja cont, cere unui coleg o invitație pe adresa {email}.
          </p>
        </div>
        <div className="space-y-3 border-2 border-cerneala bg-hartie p-4">
          <Field
            label="CUI-ul firmei"
            help={cui && !valid ? 'CUI-ul nu e valid (cifra de control).' : 'Cu sau fără „RO”.'}
          >
            <div className="flex gap-2">
              <Input
                name="cui"
                value={cui}
                inputMode="numeric"
                onChange={(e) => {
                  setCui(e.target.value);
                  setLookup('idle');
                }}
                placeholder="ex. 14399840"
              />
              <Button onClick={() => void find()} disabled={!valid || lookup === 'busy'}>
                Caută
              </Button>
            </div>
          </Field>
          {lookup === 'notfound' ? (
            <Note>ANAF nu are date pentru acest CUI. Completează manual.</Note>
          ) : null}
          {lookup === 'manual' ? (
            <Note>Datele ANAF nu sunt disponibile acum. Completează manual.</Note>
          ) : null}
          {company.inactive ? <Note>Atenție: ANAF arată firma ca inactivă fiscal.</Note> : null}
        </div>
        {ready ? (
          <form
            key={company.cui}
            onSubmit={create}
            className="space-y-3 border-2 border-cerneala bg-hartie p-4"
          >
            <Field label="Denumirea">
              <Input
                name="name"
                defaultValue={company.name ?? ''}
                required
                minLength={2}
                maxLength={200}
              />
            </Field>
            <Field label="Nr. Registrul Comerțului">
              <Input name="reg_com" defaultValue={company.reg_com ?? ''} />
            </Field>
            <Field label="Adresa sediului">
              <Input name="address" defaultValue={company.address ?? ''} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Județul">
                <Input name="county" defaultValue={company.county ?? ''} />
              </Field>
              <Field label="Localitatea">
                <Input name="city" defaultValue={company.city ?? ''} />
              </Field>
            </div>
            <Field label="Email pentru facturi" help="Gol = emailul tău.">
              <Input name="billing_email" type="email" />
            </Field>
            <Field label="Funcția ta în firmă" help="Ex. Director vânzări. Se vede de colegi.">
              <Input name="job_title" maxLength={80} />
            </Field>
            <p className="text-xs text-gri-700">
              Plătitor de TVA:{' '}
              {company.vat_payer === undefined ? 'necunoscut' : company.vat_payer ? 'da' : 'nu'}.
              Perioada de probă de 14 zile pornește când creezi firma; nu e nevoie de card.
            </p>
            <ErrorBox error={error} />
            <Button tone="primary" type="submit" disabled={busy} className="w-full">
              Creează firma
            </Button>
          </form>
        ) : (
          <ErrorBox error={error} />
        )}
      </main>
      <Footer />
    </div>
  );
}
